import React, { useEffect, useMemo, useState } from 'react';
import { relativeTime } from '../../kit';
import SectionHead from '../SectionHead';
import { apiErrorMessage, getUserUsage, isAbortError } from '../usersApi';
import { pageTitle } from './usagePages';
import {
  CHANNEL_LABELS,
  HEAT_RAMP,
  REPORT_LABELS,
  calendarWeeks,
  detailDevices,
  detailHeat,
  dowShort,
  heatLevel,
  hourLabel,
  minuteLabel,
} from './usageModel';
import './usage.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortRange = ({ from, to }) => {
  const f = (ymd) => { const [, m, d] = String(ymd).split('-').map(Number); return `${MONTHS[m - 1]} ${d}`; };
  return from && to ? `${f(from)} – ${f(to)}` : '';
};

/**
 * User panel → Usage: one person's last 30 days (hours, pages, report
 * emails) and 13-week calendar, for an organization the admin shares.
 */
export default function UsageSection({ user, directory }) {
  const orgs = useMemo(() => {
    const codes = Object.keys(user?.orgAccess || {});
    return (directory?.orgs || []).filter((o) => codes.includes(o.code));
  }, [user, directory]);
  const [org, setOrg] = useState(null);
  const orgCode = org && orgs.some((o) => o.code === org) ? org : orgs[0]?.code || null;
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!user?.id || !orgCode) return undefined;
    const ctrl = new AbortController();
    setState({ loading: true, error: null, data: null });
    getUserUsage({ id: user.id, org: orgCode }, { signal: ctrl.signal })
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => {
        if (!isAbortError(err)) setState({ loading: false, error: apiErrorMessage(err, "Couldn't load usage."), data: null });
      });
    return () => ctrl.abort();
  }, [user?.id, orgCode]);

  const data = state.data;
  const weeks = useMemo(() => calendarWeeks(data), [data]);
  const heat = useMemo(() => detailHeat(data), [data]);
  const devices = useMemo(() => detailDevices(data), [data]);

  const last30 = useMemo(() => {
    if (!data) return null;
    const cutoff = (data.calendar?.days || 0) - 30;
    const app = new Set();
    let opens = 0;
    (data.daily || []).forEach(([d, channel, events]) => {
      if (d < cutoff) return;
      if (channel === 'email') opens += Number(events) || 0;
      else app.add(d);
    });
    return { appDays: app.size, opens };
  }, [data]);

  if (!orgCode) return null;

  const orgPicker = orgs.length > 1 ? (
    <select className="ak-select" style={{ width: 'auto', height: 28 }} aria-label="Organization" value={orgCode} onChange={(e) => setOrg(e.target.value)}>
      {orgs.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
    </select>
  ) : null;

  let body;
  if (state.loading) {
    body = <div className="us-card"><div className="ak-sk" style={{ width: '60%' }} /><div className="ak-sk" style={{ width: '40%' }} /></div>;
  } else if (state.error) {
    body = <div className="us-card"><span className="ak-help">{state.error}</span></div>;
  } else if (data?.source?.error) {
    body = (
      <div className="us-card">
        <span className="ak-help">
          {data.source.error === 'not_configured' ? "Activity data isn't connected (PostHog isn't set up on the server)." : `Couldn't load activity: ${data.source.error}`}
        </span>
      </div>
    );
  } else {
    const u = data.user || {};
    const lastVia = u.lastSeenChannel ? CHANNEL_LABELS[u.lastSeenChannel] : null;
    body = (
      <>
        <div className="uu-panel-stats">
          <div className="uu-panel-stat"><span className="ak-help">App days</span><strong>{last30.appDays} <span>of 30</span></strong></div>
          <div className="uu-panel-stat"><span className="ak-help">Report emails</span><strong>{last30.opens} <span>opened</span></strong></div>
          <div className="uu-panel-stat"><span className="ak-help">Top device</span><strong style={{ fontSize: 14 }}>{devices[0]?.label || '—'}</strong></div>
          <div className="uu-panel-stat"><span className="ak-help">Last sign-in</span><strong style={{ fontSize: 14 }}>{relativeTime(user.lastSignInAt) || 'Never'}</strong>{lastVia ? <span className="ak-help">{lastVia}</span> : null}</div>
        </div>

        <div className="us-card">
          <span className="ak-section-label">When they use it · last 30 days · {data.timezone}</span>
          {heat.max ? (
            <div className="uu-mini-heat" role="img" aria-label="Hour-of-week activity for the last 30 days">
              {heat.grid.map((row, dow) => (
                <div key={dow} className="uu-heat-row">
                  <span className="uu-heat-day">{dowShort(dow)}</span>
                  {row.map((v, h) => <span key={h} className="uu-heat-cell" style={{ background: HEAT_RAMP[heatLevel(v, heat.max)] }} />)}
                </div>
              ))}
              <div className="uu-heat-row uu-heat-hours" aria-hidden="true">
                <span className="uu-heat-day" />
                {Array.from({ length: 24 }, (_, h) => <div key={h}>{h % 6 === 0 ? hourLabel(h) : ''}</div>)}
              </div>
            </div>
          ) : <span className="ak-help">No activity in the last 30 days.</span>}
        </div>

        <div className="us-card">
          <span className="ak-section-label">Last 13 weeks</span>
          <div className="uu-cal">
            <div className="uu-cal-days" aria-hidden="true">
              {['Mon', '', 'Wed', '', 'Fri', '', 'Sun'].map((d, i) => <span key={i}>{d}</span>)}
            </div>
            <div className="uu-cal-weeks" role="img" aria-label={`Active on ${(data.daily || []).length ? new Set(data.daily.map((r) => r[0])).size : 0} of the last ${data.calendar.days} days`}>
              {weeks.map((w, wi) => (
                <div key={wi} className="uu-cal-week">
                  {w.map((k, di) => <span key={di} className={k === null ? 'uu-out' : `uu-${k}`} />)}
                </div>
              ))}
            </div>
          </div>
          <div className="uu-key">
            <span><span className="uu-day uu-app" />App</span>
            <span><span className="uu-day uu-email" />Email only</span>
            <span><span className="uu-day uu-none" />None</span>
          </div>
        </div>

        {devices.length ? (
          <div className="us-card">
            <span className="ak-section-label">Devices · share of active days</span>
            <div className="uu-list">
              {devices.map((d) => (
                <div key={d.key} className="uu-list-row">
                  <span className="uu-list-label">{d.label}</span>
                  <div className="uu-meter"><span style={{ width: `${d.pct}%` }} /></div>
                  <span className="uu-list-value">{d.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {data.pages?.length ? (
          <div className="us-card">
            <span className="ak-section-label">Most used · last 30 days</span>
            <div className="uu-list">
              {data.pages.slice(0, 6).map((p) => (
                <div key={p.path} className="uu-list-row">
                  <div className="uu-list-main"><span>{pageTitle(p.path)}</span></div>
                  <span className="ak-help">{relativeTime(p.lastAt)}</span>
                  <span className="uu-list-value">{p.views} views</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {data.emails?.length ? (
          <div className="us-card">
            <span className="ak-section-label">Report emails · last 30 days</span>
            <div className="uu-list">
              {data.emails.map((e) => (
                <div key={e.report} className="uu-list-row">
                  <div className="uu-list-main">
                    <span>{REPORT_LABELS[e.report] || e.report}</span>
                    <span className="ak-help">
                      {e.typicalMinute !== null ? `Usually opened around ${minuteLabel(e.typicalMinute)}` : null}
                      {e.lastAt ? `${e.typicalMinute !== null ? ' · ' : ''}last ${relativeTime(e.lastAt)}` : null}
                    </span>
                  </div>
                  <span className="uu-list-value">{e.days} of 30 days</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="us-section" aria-labelledby="us-sec-usage">
      <SectionHead id="us-sec-usage" title="Last 30 days">
        {orgPicker}
        {data?.period ? <span className="ak-help">{shortRange(data.period)}</span> : null}
      </SectionHead>
      {body}
    </section>
  );
}
